import { message } from 'antd';
import moment from 'moment';
import { put, takeLatest, select } from 'redux-saga/effects';
import { PhaseEntityType, extractParts } from 'data/projects/phase-entity';
import { logger } from 'services';
import { getAutoCorrections } from 'utils/date-shift';
import { getPhaseList, getSprintList, getTaskList, getTemplatePhaseUpdates, getTemplateTaskUpdates } from 'utils/template-date-shift';
import { updateProjectTemplateParentOfTask } from '../..';
import { rearrangeEntitiesInProjectTemplate } from '../../actions';
import { selectAllTemplateProjectPhase, selectAllTemplateSprint, selectAllTemplateTask, selectTemplateEntity, selectTemplateTask } from '../../selectors';
import { makeDateShiftsForProjectPhases, makeDateShiftsForTasks, updateProjectPhaseTemplateEntityOrder } from '../../slices';
function* rearrangeTaskInTemplateSaga(action) {
    try {
        const { payload: actions } = action;
        for(let i = 0; i < actions.length; i += 1){
            const { entityId, parent, insertAt } = actions[i];
            const { id, entityType } = extractParts(entityId);
            const entity = yield select(selectTemplateEntity(entityId));
            yield put(updateProjectPhaseTemplateEntityOrder({
                entity,
                entityId,
                ...parent,
                insertAt
            }));
            if (entityType === PhaseEntityType.Task) {
                const task = yield select(selectTemplateTask(id));
                yield put(updateProjectTemplateParentOfTask({
                    task,
                    ...parent,
                    insertAt
                }));
            }
        }
    } catch (error) {
        logger.error('rearrangeTask failed', {
            error
        });
        message.error('There was an error in rearranging the task');
    }
}
// Recompute a phase's dates to encompass its member tasks/sprints, mirroring
// `alignParentWithChildren` from the task-edit date-shift path.
export const getPhaseDatesFromChildren = (phase, taskList, sprintList)=>{
    var _phase_tasks;
    const childIds = [
        ...((_phase_tasks = phase.tasks) !== null && _phase_tasks !== void 0 ? _phase_tasks : []).map((taskRef)=>typeof taskRef === 'number' ? taskRef : taskRef === null || taskRef === void 0 ? void 0 : taskRef.taskId).filter((id)=>id != null),
        ...Object.values(sprintList).filter((sprint)=>sprint.parent === phase.projectPhaseId).map((sprint)=>sprint.id)
    ];
    let startDate = null;
    let dueDate = null;
    childIds.forEach((childId)=>{
        var _taskList_childId;
        const child = (_taskList_childId = taskList[childId]) !== null && _taskList_childId !== void 0 ? _taskList_childId : sprintList[childId];
        if (!child) {
            return;
        }
        if (!startDate || moment(child.startDate).isBefore(moment(startDate))) {
            startDate = child.startDate;
        }
        if (!dueDate || moment(child.dueDate).isAfter(moment(dueDate))) {
            dueDate = child.dueDate;
        }
    });
    return startDate && dueDate ? {
        startDate,
        dueDate
    } : null;
};
export const resolveEnclosingPhaseId = (startTaskId, taskMap)=>{
    const seen = new Set();
    let current = startTaskId ? taskMap[startTaskId] : undefined;
    while(current){
        var _current_projectPhase, _current_parentTask;
        if ((_current_projectPhase = current.projectPhase) === null || _current_projectPhase === void 0 ? void 0 : _current_projectPhase.projectPhaseId) {
            return current.projectPhase.projectPhaseId;
        }
        const parentId = (_current_parentTask = current.parentTask) === null || _current_parentTask === void 0 ? void 0 : _current_parentTask.taskId;
        if (!parentId || seen.has(parentId)) {
            return undefined;
        }
        seen.add(parentId);
        current = taskMap[parentId];
    }
    return undefined;
};
export function* dateShiftOnTasksRearrangeSaga(action) {
    try {
        const { payload: actions } = action;
        let phases = yield select(selectAllTemplateProjectPhase());
        let tasks = yield select(selectAllTemplateTask());
        // TODO: need to refactor to handle sprints rearrange
        const sprints = yield select(selectAllTemplateSprint());
        const affectedPhaseIds = new Set();
        let hasHierarchyChanges = false;
        for(let i = 0; i < actions.length; i += 1){
            const { entityId, parent } = actions[i];
            const { id: taskId, entityType } = extractParts(entityId);
            if (entityType === PhaseEntityType.Task) {
                var _ref, _ref1;
                var _task_parentTask, _task_parentTask1, _task_projectPhase, _task_projectPhase1, _task_parentTask2, _task_parentTask3, _task_projectPhase2;
                /**
         * Update parent and child references
         */ const task = tasks[taskId];
                const { projectPhase: newParentPhase = {}, parentTask: newParentTask = {} } = {
                    ...parent
                };
                const isSameParent = (newParentTask === null || newParentTask === void 0 ? void 0 : newParentTask.taskId) != null ? (task === null || task === void 0 ? void 0 : (_task_parentTask = task.parentTask) === null || _task_parentTask === void 0 ? void 0 : _task_parentTask.taskId) === newParentTask.taskId : (task === null || task === void 0 ? void 0 : (_task_parentTask1 = task.parentTask) === null || _task_parentTask1 === void 0 ? void 0 : _task_parentTask1.taskId) == null && (task === null || task === void 0 ? void 0 : (_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId) === (newParentPhase === null || newParentPhase === void 0 ? void 0 : newParentPhase.projectPhaseId);
                if (isSameParent) {
                    continue;
                }
                hasHierarchyChanges = true;
                const sourcePhaseId = (_ref = task === null || task === void 0 ? void 0 : (_task_projectPhase1 = task.projectPhase) === null || _task_projectPhase1 === void 0 ? void 0 : _task_projectPhase1.projectPhaseId) !== null && _ref !== void 0 ? _ref : resolveEnclosingPhaseId(task === null || task === void 0 ? void 0 : (_task_parentTask2 = task.parentTask) === null || _task_parentTask2 === void 0 ? void 0 : _task_parentTask2.taskId, tasks);
                if (sourcePhaseId) {
                    affectedPhaseIds.add(sourcePhaseId);
                }
                /**
         * Remove task reference from current parent
         */ if (task === null || task === void 0 ? void 0 : (_task_parentTask3 = task.parentTask) === null || _task_parentTask3 === void 0 ? void 0 : _task_parentTask3.taskId) {
                    const currentParentTask = tasks[task.parentTask.taskId];
                    tasks = {
                        ...tasks,
                        [task.parentTask.taskId]: {
                            ...currentParentTask,
                            subTasks: currentParentTask.subTasks.filter((subTaskRef)=>subTaskRef !== taskId)
                        }
                    };
                } else if (task === null || task === void 0 ? void 0 : (_task_projectPhase2 = task.projectPhase) === null || _task_projectPhase2 === void 0 ? void 0 : _task_projectPhase2.projectPhaseId) {
                    const currentProjectPhase = phases[task.projectPhase.projectPhaseId];
                    phases = {
                        ...phases,
                        [task.projectPhase.projectPhaseId]: {
                            ...currentProjectPhase,
                            tasks: currentProjectPhase.tasks.filter((taskRef)=>taskRef !== taskId)
                        }
                    };
                }
                /**
         * Add task reference in new parent and update parent reference in task
         */ if (newParentPhase === null || newParentPhase === void 0 ? void 0 : newParentPhase.projectPhaseId) {
                    const _phase = phases[newParentPhase.projectPhaseId];
                    phases = {
                        ...phases,
                        [newParentPhase.projectPhaseId]: {
                            ..._phase,
                            tasks: [
                                taskId,
                                ..._phase.tasks
                            ]
                        }
                    };
                    tasks = {
                        ...tasks,
                        [taskId]: {
                            ...task,
                            projectPhase: _phase,
                            parentTask: undefined
                        }
                    };
                } else if (newParentTask === null || newParentTask === void 0 ? void 0 : newParentTask.taskId) {
                    const _task = tasks[newParentTask.taskId];
                    tasks = {
                        ...tasks,
                        [newParentTask.taskId]: {
                            ..._task,
                            subTasks: [
                                taskId,
                                ..._task.subTasks
                            ]
                        },
                        [taskId]: {
                            ...task,
                            parentTask: _task,
                            projectPhase: undefined
                        }
                    };
                }
                const targetPhaseId = (_ref1 = newParentPhase === null || newParentPhase === void 0 ? void 0 : newParentPhase.projectPhaseId) !== null && _ref1 !== void 0 ? _ref1 : resolveEnclosingPhaseId(newParentTask === null || newParentTask === void 0 ? void 0 : newParentTask.taskId, tasks);
                if (targetPhaseId) {
                    affectedPhaseIds.add(targetPhaseId);
                }
            }
        }
        if (!hasHierarchyChanges) {
            return;
        }
        // The corrector's output was never fed back into `tasks` between actions.
        // Correct the fully-updated hierarchy once instead of emitting transient
        // intermediate date states for every dragged task.
        const finalTaskList = getTaskList(Object.values(tasks));
        const finalSprintList = getSprintList(Object.values(sprints));
        const [phaseUpdates, taskUpdates] = getAutoCorrections(getPhaseList(Object.values(phases)), finalTaskList, finalSprintList, {
            allowWeekend: true,
            enabledNewDependencies: false
        });
        const phaseDateShifts = getTemplatePhaseUpdates(phaseUpdates);
        const taskDateShifts = getTemplateTaskUpdates(taskUpdates, finalTaskList);
        if (phaseDateShifts.length > 0) {
            yield put(makeDateShiftsForProjectPhases(phaseDateShifts));
        }
        if (taskDateShifts.length > 0) {
            yield put(makeDateShiftsForTasks(taskDateShifts));
        }
        // Encompass the tasks' final dates so a phase never ends up starting before
        // a child that getAutoCorrections shifted forward in the same run.
        Object.values(taskUpdates).forEach(({ id, startDate, dueDate })=>{
            if (finalTaskList[id]) {
                finalTaskList[id] = {
                    ...finalTaskList[id],
                    ...startDate ? {
                        startDate
                    } : {},
                    ...dueDate ? {
                        dueDate
                    } : {}
                };
            }
        });
        const phaseRealignments = [];
        affectedPhaseIds.forEach((phaseId)=>{
            const phase = phases[phaseId];
            if (!phase) {
                return;
            }
            const dates = getPhaseDatesFromChildren(phase, finalTaskList, finalSprintList);
            if (!dates) {
                return;
            }
            phaseRealignments.push({
                projectPhaseId: phaseId,
                startDate: moment(dates.startDate).format(),
                dueDate: moment(dates.dueDate).format(),
                dependentProjectPhases: []
            });
        });
        if (phaseRealignments.length > 0) {
            yield put(makeDateShiftsForProjectPhases(phaseRealignments));
        }
    } catch (error) {
        logger.error('Date shifting during rearrangeTask failed', {
            error
        });
        message.error('There was an error in rearranging the task');
    }
}
export function* watchRearrangeTaskInProjectTemplateSaga() {
    yield takeLatest(rearrangeEntitiesInProjectTemplate.type, rearrangeTaskInTemplateSaga);
    yield takeLatest(rearrangeEntitiesInProjectTemplate.type, dateShiftOnTasksRearrangeSaga);
}
